// src/components/charts/StackedColumns.tsx
//
// ★★★ WAVE 27 — COMPOSITION OVER TIME, and the reason this replaced a line chart.
//
// ⚠ **THE DECISION TRAIL WAS THREE LINES AND IT WAS THE WRONG FORM.** Approvals at
// level 1, approvals at level 2 and refusals were drawn as three overlaid lines,
// which answers "how did each move" and hides the question an operations screen is
// actually for: **how much did this office get through on a given day, and what was
// it made of.** Three lines force a reader to add them up by eye; a stacked column
// shows the total AND the mix in one mark. `choosing-a-form.md`: part-to-whole
// across an ordered axis is a stacked bar, not a multi-line.
//
// It also stopped the dashboard being four pictures of the same shape — the second
// line chart looked so like the first that a reader learned nothing from the
// difference.
//
// ⚠ **STACKING IS ONLY HONEST WHEN THE PARTS SUM TO SOMETHING REAL.** These three
// are mutually exclusive outcomes of one decision, so their sum is "decisions
// recorded that day" — a quantity that means something. Stacking series that do not
// partition a whole is the `anti-patterns.md` entry this form is most often guilty
// of, and it is why this component takes `series` that the caller has to have
// thought about rather than any array.
//
// ⚠ **A 2px SURFACE GAP BETWEEN SEGMENTS** (§5.3 rule 6) — without it two adjacent
// fills of similar lightness read as one taller segment.
import type { ReactElement } from 'react'
import { ChartFrame, type ChartLegendEntry } from './ChartFrame'

export interface StackedSeries {
  readonly label: string
  readonly slot: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8
  readonly values: readonly number[]
}

const WIDTH = 720
const HEIGHT = 220
const PAD_LEFT = 44
const PAD_BOTTOM = 26
const PAD_TOP = 10
const PAD_RIGHT = 12

function niceCeiling(max: number): number {
  if (max <= 0) return 1
  const magnitude = 10 ** Math.floor(Math.log10(max))
  for (const step of [1, 2, 2.5, 5, 10]) {
    if (magnitude * step >= max) return magnitude * step
  }
  return magnitude * 10
}

export function StackedColumns({
  title,
  subtitle,
  labels,
  series,
  labelOf,
}: {
  readonly title: string
  readonly subtitle: string
  /** One per column, in order — dates here, but the component does not assume it. */
  readonly labels: readonly string[]
  readonly series: readonly StackedSeries[]
  readonly labelOf: (label: string) => string
}): ReactElement {
  const totals = labels.map((_, i) => series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0))
  const ceiling = niceCeiling(totals.reduce((h, t) => Math.max(h, t), 0))
  const innerWidth = WIDTH - PAD_LEFT - PAD_RIGHT
  const innerHeight = HEIGHT - PAD_TOP - PAD_BOTTOM
  const slotWidth = labels.length === 0 ? 0 : innerWidth / labels.length
  // A visible gutter between columns, but never so wide that a 90-day window
  // renders as slivers.
  const barWidth = Math.max(2, Math.min(slotWidth - 2, 26))
  const yOf = (value: number): number => PAD_TOP + innerHeight - (value / ceiling) * innerHeight
  // ⚠ DEDUPED: on a small ceiling (1 or 2) rounding collapses several quarters onto the same integer (0, 0, 1, 1, 1), which
  // repeated a gridline and its label on top of itself and gave React duplicate keys.
  const ticks = [...new Set([0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(ceiling * f)))]

  const legend: ChartLegendEntry[] = series.map((s) => ({ label: s.label, slot: s.slot }))
  const columns = [
    { key: 'label', header: 'Date' },
    ...series.map((s) => ({ key: s.label, header: s.label, numeric: true })),
    { key: 'Total', header: 'Total', numeric: true },
  ]
  const rows = labels.map((label, i) => ({
    label: labelOf(label),
    ...Object.fromEntries(series.map((s) => [s.label, s.values[i] ?? 0])),
    Total: totals[i] ?? 0,
  }))

  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      legend={legend}
      columns={columns}
      rows={rows}
      emptyMessage="No day in this window carries a decision your scope can read."
    >
      <div className="pt-chart-plot">
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="w-full h-auto"
          role="img"
          aria-label={`${title}. ${subtitle}. The figures are available as a table.`}
        >
          {ticks.map((tick) => (
            <g key={tick}>
              <line className="pt-chart-grid" x1={PAD_LEFT} x2={WIDTH - PAD_RIGHT} y1={yOf(tick)} y2={yOf(tick)} />
              <text className="pt-chart-tick" x={PAD_LEFT - 8} y={yOf(tick) + 4} textAnchor="end">
                {tick.toLocaleString('en-SG')}
              </text>
            </g>
          ))}

          {labels.map((label, i) => {
            const x = PAD_LEFT + i * slotWidth + (slotWidth - barWidth) / 2
            let cursor = 0
            return (
              <g key={label}>
                <title>
                  {`${labelOf(label)}: ${series
                    .map((s) => `${s.label} ${(s.values[i] ?? 0).toLocaleString('en-SG')}`)
                    .join(', ')}`}
                </title>
                {series.map((s) => {
                  const value = s.values[i] ?? 0
                  if (value === 0) return null
                  const height = (value / ceiling) * innerHeight
                  const y = yOf(cursor + value)
                  cursor += value
                  return (
                    <rect
                      key={s.label}
                      className={`pt-series-fill-${s.slot}`}
                      x={x}
                      y={y}
                      width={barWidth}
                      /* ⚠ The 2px surface gap, taken off the TOP of each segment. */
                      height={Math.max(0, height - 2)}
                      rx={1}
                    />
                  )
                })}
              </g>
            )
          })}

          <text className="pt-chart-tick" x={PAD_LEFT} y={HEIGHT - 6}>
            {labelOf(labels[0] ?? '')}
          </text>
          <text className="pt-chart-tick" x={WIDTH - PAD_RIGHT} y={HEIGHT - 6} textAnchor="end">
            {labelOf(labels[labels.length - 1] ?? '')}
          </text>
        </svg>
      </div>
    </ChartFrame>
  )
}
